import { Sound } from "../audio/Sound.js";

export interface SoundwaveOptions {
    container?: HTMLElement;
    infos?: SoundwaveBufferInfo[];
    totalDuration?: number;
    colorLeft?: string;
    colorRight?: string;
    colorBaseLineLeft?: string;
    colorBaseLineRight?: string;
    minWaveHeight?: number;
    style?: SoundwaveStyle;
    fill?: boolean;
    shadow?: boolean;
    shadowColor?: string;
    shadowBlur?: number;
    interactive?: boolean;
    lineWidth?: number;
    baseline?: boolean;
}

export type SoundwaveStyle = "wave" | "bars-bottom";

export interface SoundwaveBufferInfo {
    sound: Sound;
    volume: number;
}

export interface SoundwaveChangeEvent {
    time: number;
}

export class Soundwave {

    public readonly element: HTMLDivElement;

    public onSeek = e5.Signal.create<[SoundwaveChangeEvent]>();

    private _options: SoundwaveOptions;
    private _time: number = 0;
    private _duration: number;
    private _left: HTMLDivElement;
    private _right: HTMLDivElement;
    private _leftCanvas: HTMLCanvasElement;
    private _rightCanvas: HTMLCanvasElement;
    private _onResize: e5.ResizeSignal = e5.Resize.create();
    private _actor: e5.Actor = null;
    private _waves: {
        canvas: HTMLCanvasElement;
        color: string;
        colorBaseline: string;
        context: CanvasRenderingContext2D
    }[] = [];

    constructor(options: SoundwaveOptions) {
        options = e5.object.merge({
            colorLeft: "#FFF",
            colorRight: "#000",
            infos: [],
            minWaveHeight: 0,
            style: "wave",
            baseline: true,
            fill: false,
            interactive: true,
            shadow: false,
            shadowBlur: 2,
            shadowColor: "rgba(0, 0, 0, 0.75)",
            lineWidth: 1,
        } as SoundwaveOptions, options);
        this._options = options;

        this.element = document.createElement("div");
        this.element.className = "soundwave";
        this.element.innerHTML = `
            <div class="soundwave__wrapper soundwave__wrapper--left">
                <canvas></canvas>
            </div>
            <div class="soundwave__wrapper soundwave__wrapper--right">
                <canvas></canvas>
            </div>
        `;

        this._left = this.element.querySelector(".soundwave__wrapper--left");
        this._leftCanvas = this._left.querySelector("canvas");
        this._right = this.element.querySelector(".soundwave__wrapper--right");
        this._rightCanvas = this._right.querySelector("canvas");

        this._waves = [
            {
                canvas: this._leftCanvas,
                color: this._options.colorLeft,
                colorBaseline: this._options.colorBaseLineLeft || this._options.colorLeft,
                context: this._leftCanvas.getContext("2d")
            },
            {
                canvas: this._right.querySelector("canvas"),
                color: this._options.colorRight,
                colorBaseline: this._options.colorBaseLineRight || this._options.colorRight,
                context: this._rightCanvas.getContext("2d")
            }
        ];

        this._onResize.element = this.element;
        this._onResize.add(() => {
            this.draw();
        });

        if (options.container) {
            options.container.appendChild(this.element);
        }

        if (options.infos && options.infos.length > 0) {
            this.infos = options.infos;
        }

        if (options.interactive) {
            this.setInteractive();
        }
    }

    private setInteractive(): void {
        const actor = new e5.Actor(this.element);
        this._actor = actor;

        const getTime = () => {
            const local = e5.Transform.toLocal(this.element, actor.pointer.x, actor.pointer.y);
            const ratio = e5.Calc.clamp(local.x / this.element.offsetWidth, 0, 1);
            return ratio * this._options.totalDuration;
        }


        actor.onDown.add(() => {
            this.updateTimeHandle(getTime());
        });

        actor.onMove.add(() => {
            this.updateTimeHandle(getTime());
        });

        actor.onClear.add(() => {
            this.onSeek.dispatch({
                time: getTime()
            });
        });
    }

    public set infos(infos: SoundwaveBufferInfo[]) {

        let duration = 0;
        for (let info of infos) {
            duration = Math.max(duration, info.sound.buffer.duration);
        }
        this._duration = duration;
        if (this._options.totalDuration === undefined) {
            this._options.totalDuration = duration;
        }

        if (this._options.totalDuration < this._duration) {
            console.warn("The given total duration is too small.");
            this._options.totalDuration = this._duration;
        }

        this._options.infos = infos;
        this.draw();
    }

    public set time(time: number) {
        time = e5.Calc.clamp(time, 0, this._duration);
        if (this._time === time) {
            return;
        }
        this._time = time;

        if (!this._actor
            || this._actor.pointers.length === 0) {
            this.updateTimeHandle(time);
        }
    }

    private updateTimeHandle(time: number): void {
        const ratio: number = time / this._duration;
        this._left.style.right = ((1 - ratio) * 100) + "%";
        this._right.style.left = ((ratio) * 100) + "%";
    }

    /**
     * @link https://stackoverflow.com/questions/22073716/create-a-waveform-of-the-full-track-with-web-audio-api
     */
    private draw() {
        const canvasWidth = this.element.offsetWidth;
        const canvasHeight = this.element.offsetHeight;

        //Falls der Track "zu" kurz ist.
        const xScale = this._duration / this._options.totalDuration;
        const count = Math.floor(canvasWidth * xScale);
        const cenY = canvasHeight / 2;

        for (let wave of this._waves) {
            wave.canvas.width = canvasWidth;
            wave.canvas.height = canvasHeight;

            wave.context.clearRect(0, 0, canvasWidth, canvasHeight);
            wave.context.strokeStyle = wave.color;
            wave.context.lineWidth = this._options.lineWidth;
            wave.context.beginPath();

            wave.context.lineJoin = "round";
            wave.context.lineCap = "round";

            // wave.context.beginPath();
            // wave.context.moveTo(0, 10);
            // wave.context.lineTo(1000, 10);
            // wave.context.stroke();
            // wave.context.beginPath();
        }

        if (this._options.style === "wave") {
            // console.log(this._options.infos);   
        }

        for (let i = 0; i <= count; i++) {

            const time = (i / count) * this._options.totalDuration;

            let x = i;

            x = Math.round(x) - 0.5;

            let y = 0;

            for (let track of this._options.infos) {
                if (time <= track.sound.buffer.duration) {


                    /**
                     * :(
                     * 
                     * Bad memory consumption ... getChannelData() will increase the overall memory very heavy!
                     * Partial getChannelData() is not possible yet ... 
                     * Is it uncompressed PCM data??? ... 
                     * 
                     * https://bugzilla.mozilla.org/show_bug.cgi?id=1305403
                     * https://w3c.github.io/webcodecs/
                     * https://github.com/Rich-Harris/phonograph
                     * https://bugs.chromium.org/p/chromium/issues/detail?id=894322#c8
                     * 
                     */

                    // const buffer = track.sound.buffer;
                    // for (let i = 0; i < buffer.numberOfChannels; ++i) {
                    //     const channel = buffer.getChannelData(i);
                    //     const audioBuffKey = Math.floor((time / buffer.duration) * channel.length);
                    //     if (channel.length > audioBuffKey) {
                    //         y = Math.max(
                    //             channel[audioBuffKey] * track.volume,
                    //             y,
                    //             0);
                    //     }
                    // }


                    const sound = track.sound;
                    const audioBuffKey = Math.floor((time / sound.buffer.duration) * sound.channel.length);
                    if (sound.channel.length > audioBuffKey) {
                        y = Math.max(
                            sound.channel[audioBuffKey] * track.volume,
                            y,
                            0);
                    }

                    // const audioBuffKey = Math.floor((time / track.sound.buffer.duration) * track.sound.channels[0].length);
                    // for (let channel of track.sound.channels) {
                    //     if (channel.length > audioBuffKey) {
                    //         y = Math.max(
                    //             channel[audioBuffKey] * track.volume,
                    //             y,
                    //             0);
                    //     }
                    // }
                }
            }

            if (this._options.style === "wave") {
                y *= canvasHeight * 2;
                y = e5.Calc.clamp(y, this._options.minWaveHeight * 0.5, (canvasHeight * 0.5) - 3);
                if (y > 0) {
                    for (let wave of this._waves) {
                        // wave.context.moveTo(x, cenY - y);
                        // wave.context.lineTo(x, cenY + y);
                        wave.context.moveTo(x, Math.round(cenY - y) - 0.5);
                        wave.context.lineTo(x, Math.round(cenY + y) - 0.5);
                    }
                }
                // console.log(canvasHeight, this._options.minWaveHeight, y);
            }
            else if (this._options.style === "bars-bottom") {
                y *= canvasHeight * 4;
                // y = Math.max(this._options.minWaveHeight * 0.5, y);
                y += this._options.minWaveHeight;
                y = Math.max(canvasHeight - y, 3);
                for (let wave of this._waves) {
                    if (i === 0) {
                        wave.context.moveTo(x, y);
                    } else {
                        wave.context.lineTo(x, y);
                    }
                }
            }
        }

        for (let wave of this._waves) {


            if (this._options.shadow) {
                wave.context.shadowColor = this._options.shadowColor;
                wave.context.shadowBlur = this._options.shadowBlur;
            }

            if (this._options.fill) {


                if (this._options.style === "bars-bottom") {
                    wave.context.lineTo(canvasWidth, canvasHeight);
                    wave.context.lineTo(0, canvasHeight);
                }

                wave.context.fillStyle = wave.color;
                wave.context.stroke();

                wave.context.shadowBlur = 0;
                wave.context.fill();
            } else {

                

                wave.context.stroke();


                if (this._options.baseline) {
                    wave.context.beginPath();
                    
                    wave.context.strokeStyle = wave.colorBaseline;
                    wave.context.moveTo(canvasWidth, canvasHeight * 0.5);
                    wave.context.lineTo(0, canvasHeight * 0.5);
                    wave.context.stroke();
                }
            }
        }
    }
}